import React, { useEffect, useState } from "react";
import { Button, Col, Divider, Input, Row, Select, Space, Tabs, TabsProps } from "antd";
import SearchDate from "../../components/SearchDate";
import moment from "moment";

import * as Request from "../../commons/request";
import * as TypeDTO from "../../commons/typeDTO";
import * as Common from "../../commons/common";
import DataChart, { ChartData, ChartElement } from "../../components/DataChart";
import { AlertType, useAlert } from "../../provider/AlertProvider";
import { useAsyncAxios, utilAxiosWithAuth } from "../../utils/customAxios";
import { ColumnsType } from "antd/es/table";
import DataTable from "../../components/DataTable";

type SearchStatsType = {
    startDate?: string;
    endDate?: string;
    groupBy: "MONTH" | "YEAR";
    // carNumber?: string;
};

type StatType = {
    name: string;
    data: ChartElement[];
};

function RentStatistics() {
    const alert = useAlert();
    const [searchGroupBy, setSearchGroupBy] = useState<string>("MONTH");
    const [searchStartDate, setSearchStartDate] = useState(moment().subtract(12, "M").format(Common.FORMAT_DATE));
    const [searchEndDate, setSearchEndDate] = useState(moment().format(Common.FORMAT_DATE));

    const [rentStatDatasWithPaid, setRentStatDatasWithPaid] = useState<Array<TypeDTO.RentStatsDto>>([]);
    const [rentStatDatasWithOperation, setRentStatDatasWithOperation] = useState<Array<TypeDTO.RentStatsDto>>([]);

    const [rentStatType, setRentStatType] = useState<string>("OPERATION");
    const [commonChartOptions, setCommonChartOptions] = useState<Record<string, any>>({
        chart: {
            height: 300,
            type: "bar",
            stacked: false,

            animations: {
                enabled: false,
                easing: "linear",
                dynamicAnimation: {
                    speed: 500,
                },
            },
        },
        xaxis: { type: "numeric" },
        yaxis: {
            labels: {
                style: { cssClass: "fw-rg fs-xs fc-title1" },
                formatter: function (val: number, index: number) {
                    return val;
                },
            },
        },
    });

    useEffect(() => {
        requestGetRentStatsWithPaidDays({
            startDate: moment(searchStartDate).startOf("month").format(Common.FORMAT_DATE),
            endDate: moment(searchEndDate).add(1, "M").startOf("month").format(Common.FORMAT_DATE),
            groupBy: searchGroupBy,
        });
        requestGetRentStatsWithOperationDays({
            startDate: moment(searchStartDate).startOf("month").format(Common.FORMAT_DATE),
            endDate: moment(searchEndDate).add(1, "M").startOf("month").format(Common.FORMAT_DATE),
            groupBy: searchGroupBy,
        });
    }, []);

    // 출차일 기준
    const requestAxiosGetRentStatsWithPaidDays = async (params: SearchStatsType) => {
        console.log(params);
        const response = await utilAxiosWithAuth().get(Request.RENT_STATS_PAID_URL, { params: params });
        return response.data;
    };

    const {
        loading: loadingGetRentStatsWithPaidDays,
        error: errorGetRentStatsWithPaidDays,
        data: resultGetRentStatsWithPaidDays,
        execute: requestGetRentStatsWithPaidDays,
    } = useAsyncAxios(requestAxiosGetRentStatsWithPaidDays);

    useEffect(() => {
        if (resultGetRentStatsWithPaidDays === null) return;
        console.log("resultGetRentStatsWithPaidDays", resultGetRentStatsWithPaidDays);

        setRentStatDatasWithPaid(resultGetRentStatsWithPaidDays.rentStats);
    }, [resultGetRentStatsWithPaidDays]);

    useEffect(() => {
        if (errorGetRentStatsWithPaidDays === null) return;

        alert.setAlert(errorGetRentStatsWithPaidDays.response.data.code, "Error", errorGetRentStatsWithPaidDays.response.data.message);
    }, [errorGetRentStatsWithPaidDays]);

    // 예약일 기준
    const requestAxiosGetRentStatsWithOperationDays = async (params: SearchStatsType) => {
        console.log(params);
        const response = await utilAxiosWithAuth().get(Request.RENT_STATS_OPERATION_URL, { params: params });
        return response.data;
    };

    const {
        loading: loadingGetRentStatsWithOperationDays,
        error: errorGetRentStatsWithOperationDays,
        data: resultGetRentStatsWithOperationDays,
        execute: requestGetRentStatsWithOperationDays,
    } = useAsyncAxios(requestAxiosGetRentStatsWithOperationDays);

    useEffect(() => {
        if (resultGetRentStatsWithOperationDays === null) return;
        console.log("resultGetRentStatsWithOperationDays", resultGetRentStatsWithOperationDays);

        setRentStatDatasWithOperation(resultGetRentStatsWithOperationDays.rentStats);
    }, [resultGetRentStatsWithOperationDays]);

    useEffect(() => {
        if (errorGetRentStatsWithOperationDays === null) return;

        alert.setAlert(errorGetRentStatsWithOperationDays.response.data.code, "Error", errorGetRentStatsWithOperationDays.response.data.message);
    }, [errorGetRentStatsWithOperationDays]);

    const onChangedSearchDate = (type: string, value: string) => {
        if (type === "startDate") setSearchStartDate(moment(value).format(Common.FORMAT_DATE));
        else if (type === "endDate") setSearchEndDate(moment(value).format(Common.FORMAT_DATE));
    };

    const onSearhRefresh = () => {
        setSearchGroupBy("MONTH");
        setSearchStartDate(moment().subtract(12, "M").startOf("month").format(Common.FORMAT_DATE));
        setSearchEndDate(moment().add(1, "M").startOf("month").format(Common.FORMAT_DATE));
    };

    const onSearhRents = () => {
        if (searchGroupBy === "MONTH") {
            requestGetRentStatsWithPaidDays({
                startDate: moment(searchStartDate).startOf("month").format(Common.FORMAT_DATE),
                endDate: moment(searchEndDate).add(1, "M").startOf("month").format(Common.FORMAT_DATE),
                groupBy: searchGroupBy,
            });
            requestGetRentStatsWithOperationDays({
                startDate: moment(searchStartDate).startOf("month").format(Common.FORMAT_DATE),
                endDate: moment(searchEndDate).add(1, "M").startOf("month").format(Common.FORMAT_DATE),
                groupBy: searchGroupBy,
            });
        } else if (searchGroupBy === "YEAR") {
            requestGetRentStatsWithPaidDays({
                startDate: moment(searchStartDate).startOf("year").format(Common.FORMAT_DATE),
                endDate: moment(searchEndDate).add(12, "M").startOf("year").format(Common.FORMAT_DATE),
                groupBy: searchGroupBy,
            });
            requestGetRentStatsWithOperationDays({
                startDate: moment(searchStartDate).startOf("year").format(Common.FORMAT_DATE),
                endDate: moment(searchEndDate).add(12, "M").startOf("year").format(Common.FORMAT_DATE),
                groupBy: searchGroupBy,
            });
        }
    };

    const displayStats = (rentStatsData: Array<TypeDTO.RentStatsDto>) => {
        if (rentStatsData === null) return <h2 style={{ textAlign: "center", fontWeight: "normal" }}>데이터가 없습니다! ㄟ(´･ω･`)ㄏ</h2>;
        return rentStatsData.map((statData) => {
            const reservedDays: ChartElement[] = [];
            const notReservedDays: ChartElement[] = [];
            const holidays: ChartElement[] = [];

            const columns: ColumnsType<StatType> = [
                {
                    title: "구분",
                    dataIndex: "name",
                    rowScope: "row",
                    width: "12%",
                },
            ];
            if (rentStatType === "OPERATION" && statData.rentStatsWithOperation !== null) {
                statData.rentStatsWithOperation.map((rentstatsdata) => {
                    const time =
                        rentstatsdata.month && rentstatsdata.year ? rentstatsdata.year + "년 " + rentstatsdata.month : rentstatsdata.year.toString();
                    reservedDays.push({
                        x: time,
                        y: rentstatsdata.reservedDays,
                    });
                    notReservedDays.push({
                        x: time,
                        y: rentstatsdata.notReservedDays,
                    });
                    holidays.push({
                        x: time,
                        y: rentstatsdata.holidays,
                    });
                    columns.push({
                        title: (
                            <p style={{ width: "100%", textAlign: "center", margin: 0 }}>
                                {time}
                                {rentstatsdata.month ? "월" : "년"}
                            </p>
                        ),
                        key: "data",
                        dataIndex: "data",
                        render: (_, { data }) => {
                            return data.map((d) => {
                                if (d.x === time) {
                                    return (
                                        <p style={{ width: "100%", textAlign: "center", margin: 0 }} key={d.x}>
                                            {d.y}일
                                        </p>
                                    );
                                }
                            });
                        },
                    });
                });
            } else if (rentStatType === "PAID" && statData.rentStatsWithPaid !== null) {
                statData.rentStatsWithPaid.map((rentstatsdata) => {
                    const time =
                        rentstatsdata.month && rentstatsdata.year ? rentstatsdata.year + "년 " + rentstatsdata.month : rentstatsdata.year.toString();
                    reservedDays.push({
                        x: time,
                        y: rentstatsdata.paidDays,
                    });
                    notReservedDays.push({
                        x: time,
                        y: rentstatsdata.notPaidDays,
                    });
                    columns.push({
                        title: (
                            <p style={{ width: "100%", textAlign: "center", margin: 0 }}>
                                {time}
                                {rentstatsdata.month ? "월" : "년"}
                            </p>
                        ),
                        key: "data",
                        dataIndex: "data",
                        render: (_, { data }) => {
                            return data.map((d) => {
                                if (d.x === time) {
                                    return (
                                        <p style={{ width: "100%", textAlign: "center", margin: 0 }} key={d.x}>
                                            {d.y}일
                                        </p>
                                    );
                                }
                            });
                        },
                    });
                });
            }

            const stat: Array<StatType> =
                rentStatType === "OPERATION"
                    ? [
                          {
                              name: "예약일",
                              data: reservedDays,
                          },
                          {
                              name: "미예약일",
                              data: notReservedDays,
                          },
                          {
                              name: "자체예약일",
                              data: holidays,
                          },
                      ]
                    : [
                          { name: "출차일", data: reservedDays },
                          {
                              name: "휴차일",
                              data: notReservedDays,
                          },
                      ];

            return (
                <div key={statData.carNumber}>
                    <Row justify={"space-between"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>{statData.carNumber}</span>
                            </Divider>
                        </Col>
                    </Row>
                    <DataChart height="200px" series={stat} option={commonChartOptions} />
                    <DataTable hasScroll={false} columns={columns} dataSource={stat} rowKey="name" pagination={false} />
                </div>
            );
        });
    };

    const statTabsItems: TabsProps["items"] = [
        {
            key: "OPERATION",
            label: "예약일 기준",
            children: (
                <Space size={20} direction="vertical" style={{ width: "100%", padding: "0 20px" }}>
                    {displayStats(rentStatDatasWithOperation)}
                </Space>
            ),
        },
        {
            key: "PAID",
            label: "출차일 기준",
            children: (
                <Space size={20} direction="vertical" style={{ width: "100%", padding: "0 20px" }}>
                    {displayStats(rentStatDatasWithPaid)}
                </Space>
            ),
        },
    ];

    return (
        <div className="container">
            <Space size={30} direction="vertical" style={{ width: "100%" }}>
                <h3 className="title">통계</h3>
                <div>
                    <Row justify={"space-between"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>차량별 운영 현황</span>
                            </Divider>
                        </Col>
                    </Row>
                    <Space>
                        <Select
                            style={{ width: 150 }}
                            placeholder="전체"
                            value={searchGroupBy}
                            onChange={(value) => {
                                setSearchGroupBy(value);
                            }}
                        >
                            <Select.Option value={"MONTH"}>월별</Select.Option>
                            <Select.Option value={"YEAR"}>연별</Select.Option>
                        </Select>
                        <SearchDate
                            startDate={searchStartDate}
                            endDate={searchEndDate}
                            onSearch={onChangedSearchDate}
                            picker={searchGroupBy === "MONTH" ? "month" : "year"}
                            format={searchGroupBy === "MONTH" ? "YYYY-MM" : "YYYY"}
                        />
                        <Button onClick={onSearhRefresh}>검색 초기화</Button>
                        <Button onClick={onSearhRents}>검색</Button>
                    </Space>
                </div>
                <Tabs
                    className="centeredTab"
                    style={{ background: "white" }}
                    type="card"
                    defaultActiveKey="1"
                    items={statTabsItems}
                    size="small"
                    onChange={(v) => {
                        setRentStatType(v);
                    }}
                />
            </Space>
        </div>
    );
}

export default RentStatistics;
